// Modelo de saliencia del panel de insights de postventa
// (docs/exploracion-fase2-seed-panel-chat.md §4.1–4.3).
//
// Puro: sin IO, sin reloj implícito. Las señales se calculan al leer en
// core-api (lib/insights/**, con los predicados de fase de
// lib/observation-phase-sql.ts); acá vive sólo el vocabulario, la
// fórmula de score y las reglas de ranking, para que el servidor y
// cualquier test las compartan sin re-encodearlas.
//
// Recorte del founder (2026-09-30): fuera la señal 11 (duplicados), las
// 9, 12 y 14, y toda persistencia de estado de tarjeta (sin
// insight_card_states, sin vista/descartada, sin factor novedad). Por
// eso la fórmula de §4.2 pierde el término `novedad`.
//
// Los pesos no van en la receta (ADR-0019) ni todavía en
// organizations.config (esquema .strict()): se tipa `insightWeights`
// cuando un cliente lo pida. Sin calibrar — se calibran contra el seed.
// ── Vocabulario ─────────────────────────────────────────────────────
/** Señales vigentes, en el orden del catálogo (§4.1). El orden también
 *  es el desempate estable del ranking. Los números del catálogo que
 *  faltan (9, 11, 12, 14) fueron cortados por el founder. */
export const INSIGHT_SIGNALS = [
    "emergencia_sin_visita", // 1
    "sla_por_vencer", // 2
    "sla_vencida", // 3
    "esperando_jefe", // 4
    "acta_abierta", // 5
    "defecto_sistemico", // 6
    "unidad_en_riesgo", // 7
    "contratista_fuera_de_rango", // 8
    "alza_especialidad", // 10
    "proyecto_sano", // 13
];
export const INSIGHT_SIGNAL_KIND = {
    emergencia_sin_visita: "factual",
    sla_por_vencer: "factual",
    sla_vencida: "factual",
    esperando_jefe: "factual",
    acta_abierta: "factual",
    defecto_sistemico: "estadistica",
    unidad_en_riesgo: "factual",
    contratista_fuera_de_rango: "estadistica",
    alza_especialidad: "estadistica",
    proyecto_sano: "estadistica",
};
export const INSIGHT_SEVERITIES = ["critica", "alta", "media", "baja", "positiva"];
export const INSIGHT_ACTIONABILITY = ["un_clic", "investigar", "informativa"];
/** Buckets de urgencia: plazo vencido, vence en ≤3 d, en ≤7 d, sin
 *  plazo, y `tendencia` para señales de tendencia que no tienen plazo. */
export const INSIGHT_URGENCIES = ["vencida", "lt3d", "lt7d", "sin_fecha", "tendencia"];
export const DEFAULT_INSIGHT_WEIGHTS = {
    severity: { critica: 5, alta: 4, media: 3, baja: 2, positiva: 1 },
    urgency: { vencida: 1.5, lt3d: 1.3, lt7d: 1.0, sin_fecha: 0.6, tendencia: 0.4 },
    confidence: [
        { minN: 10, factor: 1.0 },
        { minN: 5, factor: 0.7 },
        { minN: 3, factor: 0.5 },
        { minN: 0, factor: 0.3 },
    ],
    actionability: { un_clic: 1.0, investigar: 0.7, informativa: 0.5 },
    trendClamp: { min: -0.5, max: 1 },
    trendWeight: 0.5,
};
/** Rango numérico de cada severidad, para comparar contra «≥ media». */
export const INSIGHT_SEVERITY_RANK = {
    critica: 5,
    alta: 4,
    media: 3,
    baja: 2,
    positiva: 1,
};
// ── Confianza y «pocos casos» ───────────────────────────────────────
/** Bajo este n la tarjeta lleva el texto «Pocos casos (n=…): tómalo
 *  como una pista». Conteos crudos, sin intervalo de Wilson (postergado). */
export const FEW_CASES_N = 5;
export function fewCases(n) {
    return !(n >= FEW_CASES_N);
}
/** Factor de confianza por tamaño de muestra. Un n no finito o negativo
 *  cae en el bucket más bajo.
 *
 *  Modela incertidumbre estadística, así que `insightScore` sólo lo
 *  aplica a las señales `estadistica` (INSIGHT_SIGNAL_KIND). Una señal
 *  factual — una emergencia sin visita, una vencida — es cierta con
 *  n=1 y usa confianza 1,0: si no, un solo caso crítico caería bajo el
 *  umbral. */
export function insightConfidence(n, weights = DEFAULT_INSIGHT_WEIGHTS) {
    const buckets = weights.confidence;
    for (const b of buckets) {
        if (n >= b.minN)
            return b.factor;
    }
    return buckets[buckets.length - 1]?.factor ?? 0;
}
/** score = severidad × urgencia × confianza × accionabilidad ×
 *  (1 + 0,5 × tendencia). Sin factor novedad (cortado con el estado de
 *  tarjeta). La confianza es 1,0 para las señales factuales. */
export function insightScore(input, weights = DEFAULT_INSIGHT_WEIGHTS) {
    const rawTrend = input.trend !== undefined && Number.isFinite(input.trend) ? input.trend : 0;
    const trend = Math.min(weights.trendClamp.max, Math.max(weights.trendClamp.min, rawTrend));
    return (weights.severity[input.severity] *
        weights.urgency[input.urgency] *
        (INSIGHT_SIGNAL_KIND[input.signal] === "estadistica"
            ? insightConfidence(input.n, weights)
            : 1) *
        weights.actionability[input.actionability] *
        (1 + weights.trendWeight * trend));
}
// ── Ranking ─────────────────────────────────────────────────────────
export const INSIGHT_SCORE_THRESHOLD = 2.5;
/** Tope de tarjetas del panel. Desde la decisión del founder del
 *  2026-09-30 cada tarjeta es una SEÑAL (agrupa todos sus casos), así que
 *  el tope equivale a «hasta 7 tipos de señal distintos a la vista». */
export const INSIGHT_CARD_CAP = 7;
/** Casos que una tarjeta lista adentro; el resto viaja como `moreCount`
 *  («y N más»). El recorte lo hace quien arma el wire, no el ranking:
 *  los totales de la tarjeta cuentan todos los casos, no sólo los 10. */
export const INSIGHT_MEMBER_CAP = 10;
const SIGNAL_ORDER = new Map(INSIGHT_SIGNALS.map((s, i) => [s, i]));
function signalOrder(signal) {
    return SIGNAL_ORDER.get(signal) ?? Number.MAX_SAFE_INTEGER;
}
function compareRankable(a, b) {
    if (a.score !== b.score)
        return b.score - a.score;
    const sa = signalOrder(a.signal);
    const sb = signalOrder(b.signal);
    if (sa !== sb)
        return sa - sb;
    return a.subjectKey < b.subjectKey ? -1 : a.subjectKey > b.subjectKey ? 1 : 0;
}
/** Filtra, agrupa por señal, ordena y recorta según §4.3 con la
 *  agrupación que decidió el founder (2026-09-30: «una tarjeta por tipo
 *  de señal, con el detalle adentro»):
 *
 *  1. Por caso: descarta scores no finitos, y score < umbral (2,5) sólo
 *     en las señales factuales (INSIGHT_SIGNAL_KIND). Las estadísticas
 *     (6, 8, 10, 13) ya pasaron su propia compuerta de significancia
 *     dentro del detector (≥3 unidades, n≥5 y >2× la mediana, etc.);
 *     aplicarles además el umbral era un doble filtro que, con sus
 *     factores de urgencia y confianza, las dejaba inalcanzables. Para
 *     ellas el score sólo ordena.
 *  2. Agrupa los casos que pasaron por señal: una tarjeta por señal, con
 *     score = el máximo de sus casos y severidad = la más alta. Los casos
 *     quedan por score desc (desempate por subjectKey).
 *  3. Ordena las tarjetas por score desc; desempate estable por orden de
 *     señal.
 *  4. Tope 7. Primero entran las tarjetas no positivas; las positivas
 *     (proyecto sano) sólo rellenan el espacio que quede, así que nunca
 *     desplazan a una tarjeta de severidad ≥ media.
 *
 *  La antigua regla «máximo 2 por (proyecto, señal)» desapareció con la
 *  agrupación: existía para que 7 emergencias sueltas no taparan el resto
 *  del panel, y ahora todas viven en una sola tarjeta (decisión del
 *  founder 2026-09-30).
 *
 *  Sin relleno: si pasan menos tarjetas que el tope se devuelven las que
 *  pasan. Un panel con una sola tarjeta real es más honesto que uno con
 *  dos de relleno. */
export function rankInsights(candidates, opts = {}) {
    const threshold = opts.threshold ?? INSIGHT_SCORE_THRESHOLD;
    const cap = opts.cap ?? INSIGHT_CARD_CAP;
    const passing = candidates
        .filter((c) => Number.isFinite(c.score) &&
        (INSIGHT_SIGNAL_KIND[c.signal] === "estadistica" || c.score >= threshold))
        .sort(compareRankable);
    const bySignal = new Map();
    for (const c of passing) {
        const list = bySignal.get(c.signal);
        if (list)
            list.push(c);
        else
            bySignal.set(c.signal, [c]);
    }
    const groups = [...bySignal.entries()].map(([signal, members]) => ({
        signal,
        // `passing` ya viene ordenado: el primer caso es el de mayor score.
        score: members[0].score,
        severity: members.reduce((max, m) => INSIGHT_SEVERITY_RANK[m.severity] > INSIGHT_SEVERITY_RANK[max] ? m.severity : max, members[0].severity),
        members,
    }));
    groups.sort((a, b) => a.score !== b.score ? b.score - a.score : signalOrder(a.signal) - signalOrder(b.signal));
    const out = [];
    for (const g of groups)
        if (out.length < cap && g.severity !== "positiva")
            out.push(g);
    for (const g of groups)
        if (out.length < cap && g.severity === "positiva")
            out.push(g);
    return out;
}
// ── SLA ─────────────────────────────────────────────────────────────
/** Espejo del default de `providers.default_sla_days`
 *  (apps/core-api/src/db/schema.ts, `defaultSlaDays: integer(...).default(30)`).
 *  Si cambia uno, cambia el otro. */
export const DEFAULT_SLA_DAYS = 30;
const MS_PER_DAY = 86_400_000;
/** Plazo de una observación con asignado, en días corridos.
 *
 *  El reloj parte en el último `assigned`/`reassigned`: reasignar lo
 *  reinicia, y termina al llegar a `resuelta`. SLA por severidad queda
 *  postergado (founder 2026-09-30) — hoy un solo plazo por contratista.
 *
 *  Aritmética en milisegundos UTC (días de 24 h exactas): un cambio de
 *  horario en America/Santiago no corre el plazo una hora. */
export function slaDeadline(lastAssignedAt, slaDays) {
    if (!Number.isFinite(slaDays)) {
        throw new RangeError(`slaDays debe ser finito, llegó ${slaDays}`);
    }
    return new Date(lastAssignedAt.getTime() + slaDays * MS_PER_DAY);
}
// ── Bandera léxica de escalamiento (señal 7c) ───────────────────────
/** Fuente del patrón para Postgres `~*` (insensible a mayúsculas por el
 *  operador). Sólo usa sintaxis común a ARE de Postgres y a RegExp de JS
 *  (alternancia, clases, `\s`), para que las dos gemelas digan lo mismo.
 *  Es léxica: no distingue «voy al SERNAC» de «mi vecino fue al SERNAC».
 *  La tarjeta muestra el indicador, nunca el texto. */
export const ESCALATION_PATTERN = "sernac|abogad[oa]|demanda|redes\\s+sociales|funa";
/** Gemela JS de ESCALATION_PATTERN (flag `i` = el `*` de `~*`). */
export const ESCALATION_REGEX = new RegExp(ESCALATION_PATTERN, "i");
//# sourceMappingURL=insights.js.map